import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import EventPassArt from '../../../assets/vipSubscription/feature-event-pass.png';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import EventPanel from './EventPanel';
import {
	EVENT_FONT,
	eventColors,
	eventGlow,
	GREEN_BUTTON_FILL,
	UNLOCK_BUTTON_FILL,
} from './eventTheme';

// Figma "Unlock Premium Track / banner" (3324:18976): 360x96 - artwork, copy, and a two-part
// button whose lower half carries the price.
export const UNLOCK_DESIGN_WIDTH = 360;
export const UNLOCK_DESIGN_HEIGHT = 96;

type Props = {
	price: string;
	benefits: string[];
	width: number;
	onPress?: () => void;
};

const GradientFill = ({ stops, radius }: { stops: Array<[string, string]>; radius: number }) => {
	const fillId = useSvgId('eventUnlockFill');
	return (
		<View pointerEvents='none' style={[styles.fill, { borderRadius: radius }]}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						{stops.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
			</Svg>
		</View>
	);
};

export const UnlockPremiumBanner = ({ price, benefits, width, onPress }: Props) => {
	const { t } = useTranslation();
	const scale = width / UNLOCK_DESIGN_WIDTH;
	const sized = useMemo(() => createStyles(scale), [scale]);

	return (
		<EventPanel
			radius={10 * scale}
			borderWidth={Math.max(1, 1 * scale)}
			style={{ width, height: UNLOCK_DESIGN_HEIGHT * scale }}
		>
			<View style={sized.row}>
				<Image source={EventPassArt} style={sized.artwork} resizeMode='contain' />

				<View style={sized.copy}>
					<Text numberOfLines={1} style={sized.title}>
						{t('T_SITE_EVENT_UNLOCK_PREMIUM_TITLE', { defaultValue: 'Unlock Premium Track' })}
					</Text>
					<Text numberOfLines={1} style={sized.subtitle}>
						{t('T_SITE_EVENT_UNLOCK_PREMIUM_SUBTITLE', {
							defaultValue: 'and get more valuable rewards!',
						})}
					</Text>
					{benefits.map(benefit => (
						<Text key={benefit} numberOfLines={1} style={sized.benefit}>
							{`★ ${benefit}`}
						</Text>
					))}
				</View>

				<Pressable accessibilityRole='button' onPress={onPress} style={sized.button}>
					<View style={sized.buttonTop}>
						<GradientFill stops={UNLOCK_BUTTON_FILL} radius={5 * scale} />
						<Text numberOfLines={1} style={sized.buttonLabel}>
							{t('T_SITE_EVENT_UNLOCK_PASS', { defaultValue: 'UNLOCK PASS' })}
						</Text>
					</View>
					<View style={sized.buttonBottom}>
						<GradientFill stops={GREEN_BUTTON_FILL} radius={5 * scale} />
						<Text numberOfLines={1} style={sized.priceLabel}>
							{price}
						</Text>
					</View>
				</Pressable>
			</View>
		</EventPanel>
	);
};

const styles = StyleSheet.create({
	fill: {
		...StyleSheet.absoluteFillObject,
		overflow: 'hidden',
	},
});

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			flex: 1,
			flexDirection: 'row',
			alignItems: 'center',
			paddingHorizontal: 10 * scale,
			gap: 10 * scale,
		},
		artwork: {
			width: 76 * scale,
			height: 76 * scale,
		},
		copy: {
			flex: 1,
		},
		title: {
			color: eventColors.gold,
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 16 * scale,
		},
		subtitle: {
			color: eventColors.white,
			fontFamily: EVENT_FONT,
			fontSize: 10 * scale,
			fontWeight: '600',
			lineHeight: 13 * scale,
		},
		// Figma: the star is text/accent, the words gold/100.
		benefit: {
			color: eventColors.goldSoft,
			fontFamily: EVENT_FONT,
			fontSize: 10 * scale,
			fontWeight: '500',
			lineHeight: 13 * scale,
		},
		button: {
			width: 96 * scale,
			height: 62 * scale,
			borderRadius: 9 * scale,
			borderWidth: Math.max(1, 1.5 * scale),
			borderColor: eventColors.passButtonBorder,
			overflow: 'hidden',
			...eventGlow.passButton,
		},
		buttonTop: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		buttonBottom: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		buttonLabel: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '700',
			letterSpacing: 0.12 * scale,
			lineHeight: 16 * scale,
		},
		priceLabel: {
			color: eventColors.goldPale,
			fontFamily: EVENT_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 20 * scale,
		},
	});

export default UnlockPremiumBanner;
